import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Sunset, Calendar, Stethoscope, Wallet, Calculator, Clock, PieChart, ArrowRight, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const RetirementPage = () => {
  const benefits = [
    {
      icon: Calendar,
      title: "Plan for 30+ Years of Retirement, Not Just 5",
      description: "Retirement today could last 30-35 years — that's a whole career in itself. We help you:",
      points: [
        "Estimate future expenses accurately with inflation projections",
        "Plan asset drawdowns to avoid premature depletion",
        "Adjust for different lifestyle phases post-retirement"
      ]
    },
    {
      icon: Stethoscope,
      title: "Account for Healthcare & Emergency Costs",
      description: "Long-term health care is one of the most underestimated retirement expenses. We factor in:",
      points: [
        "Medical inflation and insurance adequacy",
        "Emergency reserve funds and critical illness riders",
        "Cash flow continuity during care-intensive years"
      ]
    },
    {
      icon: Wallet,
      title: "Build a Personalized Retirement Corpus Strategy",
      description: "One size doesn't fit all. Your retirement corpus depends on your lifestyle goals, dependents, and financial history. We help:",
      points: [
        "Analyze current cash flows and savings patterns",
        "Project required corpus using scenario-based modelling",
        "Implement SIPs, SWPs and rebalancing strategies"
      ]
    }
  ];

  const calculators = [
    {
      icon: Calculator,
      title: "Retirement Planner",
      color: "from-orange-500 to-red-500",
      features: ["Retirement Corpus: Set your retirement target", "Monthly Expenses: Estimate monthly needs", "SIP Planning: Calculate monthly investment"],
      link: "/calculators"
    },
    {
      icon: Clock,
      title: "Deferred SWP",
      color: "from-amber-500 to-orange-500",
      features: ["Retirement Corpus: Know your target fund", "SWP: Get your monthly withdrawal amount", "Plan Smart: Secure your future with SIP & lumpsum"],
      link: "/calculators"
    },
    {
      icon: Wallet,
      title: "Immediate SWP",
      color: "from-yellow-500 to-amber-500",
      features: ["SWP: Calculate your monthly retirement income", "Corpus: Set your required retirement fund", "Strategy: Optimize withdrawals for stability"],
      link: "/calculators"
    },
    {
      icon: PieChart,
      title: "Portfolio Planner",
      color: "from-red-500 to-pink-500",
      features: ["Asset Allocation: Plan investments across classes", "Corpus: Set your retirement goal", "SWP: Estimate post-retirement withdrawals"],
      link: "/calculators"
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 mb-6">
                <Sunset className="w-4 h-4 text-orange-400" />
                <span className="text-white/60 text-sm">Retirement Planning</span>
              </div>

              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Secure Your Sunset Years
              </h1>

              <p className="text-lg text-white/60 leading-relaxed mb-6">
                Retirement isn't the end — it's a long, vibrant chapter of life. But with people living longer and retiring earlier, it's more critical than ever to have a strategy that makes your wealth last, without compromising on lifestyle or dignity.
              </p>

              <p className="text-white/80 mb-8">
                At <span className="text-orange-400 font-semibold">Frontier Investments</span>, we help you build a retirement plan that aligns with your income, health care needs, lifestyle expectations, and family responsibilities — so your golden years are truly golden.
              </p>

              <Link
                to="/contact"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black font-semibold rounded-full hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all duration-500 group"
              >
                Plan Your Retirement
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1532619187608-e5375cab36aa?w=800&h=600&fit=crop"
                alt="Retirement Planning"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div
                  key={index}
                  className="p-8 bg-gray-50 rounded-3xl border border-gray-100 hover:shadow-xl transition-all duration-500"
                >
                  <div className="p-4 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl w-fit mb-6">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 mb-6">{benefit.description}</p>
                  <ul className="space-y-3">
                    {benefit.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="p-1 bg-orange-100 rounded-full mt-1">
                          <Check className="w-3 h-3 text-orange-600" />
                        </div>
                        <span className="text-gray-600 text-sm">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Calculators Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Plan Your Retirement With Our Calculators
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Use our retirement calculators to estimate your corpus and plan withdrawals
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {calculators.map((calc, index) => {
              const IconComponent = calc.icon;
              return (
                <Link
                  key={index}
                  to={calc.link}
                  className="group p-6 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-500"
                >
                  <div className={`p-3 bg-gradient-to-br ${calc.color} rounded-xl w-fit mb-4`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-4">{calc.title}</h3>
                  <ul className="space-y-2">
                    {calc.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                        <span className="text-white/60 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">
            Ready to secure your retirement?
          </h2>
          <p className="text-gray-500 mb-10">
            Let our experts create a personalized retirement plan for you
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-black text-white font-semibold rounded-full hover:shadow-lg transition-all duration-500 group"
          >
            Book a Consultation
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RetirementPage;
